"use client";

import { useEffect } from "react";
import { reportJsError } from "@/lib/js-error-beacon";

/**
 * Graceful route-level error UI (rule 58 — error states).
 *
 * Next.js `error.tsx` receives the thrown `error` + a `reset()` that re-renders
 * the segment. We give the seeker a clear, on-brand recovery path (Try again →
 * `reset()`, plus a Reload fallback) instead of a blank screen, and feed the
 * owned js_error beacon so the failure is queryable (events_raw) the same way
 * `ErrorBoundary` does. Height is reserved so the swap from content → error
 * does not collapse the layout.
 */
export default function RouteError({
  error,
  reset,
  boundary = "route_error",
  title = "Something went wrong",
  message = "We could not load this page. Please try again.",
}: {
  error: Error & { digest?: string };
  reset: () => void;
  boundary?: string;
  title?: string;
  message?: string;
}) {
  useEffect(() => {
    reportJsError({
      error_message: error.message || error.name || "Route render error",
      error_name: error.name || "Error",
      error_stack: error.stack || "",
      boundary,
      component: error.digest ?? undefined,
    });
  }, [error, boundary]);

  return (
    <div
      role="alert"
      className="mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col items-center justify-center px-4 py-12 text-center"
    >
      <h1 className="font-serif text-2xl font-bold" style={{ color: "var(--ish-dark)" }}>
        {title}
      </h1>
      <p className="mt-2 max-w-md text-sm" style={{ color: "var(--ish-text-muted)" }}>
        {message}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg px-5 py-2.5 text-sm font-semibold transition hover:opacity-90"
          style={{ background: "var(--ish-accent)", color: "#fff" }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") window.location.reload();
          }}
          className="rounded-lg px-5 py-2.5 text-sm font-medium transition hover:opacity-80"
          style={{ border: "1px solid var(--ish-border)", color: "var(--ish-text-muted)" }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
